import { useQueryClient } from '@tanstack/react-query'
import FbButton from 'components/FbUI/FbButton'
import { FBPage } from 'components/FbUI/Page/FBPage'
import ImportContactModal from 'components/ImportContactFlow/ImportContactModal'
import { ContactRequestsTable } from 'components/Tables/ContactRequestsTable/ContactRequestsTable'
import { ContactsTable } from 'components/Tables/ContactsTable/ContactsTable'
import useDocumentTitle from 'components/useDocumentTitle'
import React from 'react'
import { CgSoftwareUpload } from 'react-icons/cg'
import { FaPlus } from 'react-icons/fa'
import styled from 'styled-components/macro'
import { Tabs } from '../../../components/FbUI/Tabs'
import NewContactModal from '../../../components/Modals/NewContactModal'
import {
  CONTACT_REQUEST_JOB_TABLE_KEY,
  CONTACTS_TABLE_KEY,
} from '../../../constants/tableQueryKeys'
import { useNavigate, useParams } from 'react-router-dom'
import { useNavbarStore } from '../../../components/Layout/navbar-store'
import {
  FBPageInnerTabsContainer,
  FBPageTableContainer,
  FBPageTitleRow,
} from '../../../components/FbUI/Page/styles'
import { CreditBalanceCard } from 'components/Credits/CreditBalanceCard'
import { toast } from 'react-toastify'
import { useHasUnlimitedCredits } from '../../../hooks/useHasUnlimitedCredits'

export function ContactsPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const params = useParams()
  const selectedTab = (params?.type ?? 'all') as 'all' | 'requests'
  const store = useNavbarStore()
  const hasUnlimitedCredits = useHasUnlimitedCredits()

  const [totalContacts, setTotalContacts] = React.useState<number | null>(null)
  const [showNewContactModal, setShowNewContactModal] = React.useState(false)
  const [showImportContactModal, setShowImportContactModal] =
    React.useState(false)

  const handleContactCreated = () => {
    queryClient.invalidateQueries({ queryKey: ['tags', 'contacts'] })
    queryClient.refetchQueries({ queryKey: [CONTACTS_TABLE_KEY] })
  }

  const handleContactUpdated = () => {
    queryClient.refetchQueries({ queryKey: [CONTACTS_TABLE_KEY] })
  }

  const selectedTabTitle = selectedTab === 'all' ? 'All' : 'Requests'

  useDocumentTitle(
    `${selectedTab === 'all' ? 'All' : 'Requested'} Contacts - Contacts - First Bite`
  )

  // Check for success or canceled parameters in URL from Stripe redirect
  React.useEffect(() => {
    if (selectedTab === 'requests') {
      const urlParams = new URLSearchParams(window.location.search)
      const success = urlParams.get('success')
      const canceled = urlParams.get('canceled')

      console.log('Checking URL params:', {
        success,
        canceled,
        search: window.location.search,
      })

      if (success === 'true') {
        console.log('Credit purchase successful, showing success toast')
        toast.success('Credits purchased successfully!')

        // Clear the URL parameters
        if (window.history && window.history.replaceState) {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          )
        }

        // If there was a localStorage callback marker, use it and remove it
        const hasCallback =
          localStorage.getItem('creditPurchaseSuccessCallback') === 'true'
        console.log('Credit purchase callback status:', { hasCallback })

        if (hasCallback) {
          localStorage.removeItem('creditPurchaseSuccessCallback')
          console.log('Refreshing credit information after purchase')
          // Refresh credit information
          queryClient.invalidateQueries({
            queryKey: ['creditBalance'],
          })
        }
      } else if (canceled === 'true') {
        console.log('Credit purchase canceled, showing info toast')
        toast.info('Credit purchase was canceled')

        // Clear the URL parameters
        if (window.history && window.history.replaceState) {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          )
        }
      }
    }
  }, [selectedTab, queryClient])

  return (
    <FBPage>
      <div
        className={`flex flex-row items-start justify-between w-full mb-3 ${selectedTab === 'all' || hasUnlimitedCredits ? 'h-[50px]' : 'h-[80px]'}`}
      >
        <FBPageTitleRow>
          <div className="text-2xl leading-[28px] text-fg-dark mb-3">
            Contacts: <b>{selectedTabTitle}</b>
          </div>

          {selectedTab === 'all' && (
            <div className={'flex flex-row gap-2.5 ml-auto'}>
              <Button onClick={() => setShowNewContactModal(true)}>
                <FaPlus size={14} /> Add Contact
              </Button>
              <Button
                onClick={() => setShowImportContactModal(true)}
                variant="secondary"
              >
                <CgSoftwareUpload size={18} /> <span>Upload Contacts</span>
              </Button>
            </div>
          )}
        </FBPageTitleRow>

        {selectedTab === 'requests' && <CreditBalanceCard className="mb-4" />}

        {showImportContactModal && (
          <ImportContactModal
            show={showImportContactModal}
            handleClose={() => setShowImportContactModal(false)}
            onContactsImported={handleContactCreated}
          />
        )}

        {showNewContactModal && (
          <NewContactModal
            show={showNewContactModal}
            handleClose={() => setShowNewContactModal(false)}
            onContactCreated={handleContactCreated}
          />
        )}
      </div>

      <FBPageInnerTabsContainer>
        <Tabs
          tabs={[
            {
              key: 'all',
              title: (
                <div>
                  <b>All contacts</b>{' '}
                  {totalContacts ? `(${totalContacts.toLocaleString()})` : ''}
                </div>
              ),
            },
            {
              key: 'requests',
              title: (
                <div>
                  <b>Contact Requests</b>
                </div>
              ),
            },
          ]}
          selectedTab={selectedTab}
          setSelectedTab={(value) => {
            const url = `/contacts/${value}`
            navigate(url)
            store.setLastContactVisited(url)
          }}
          tabStyle={{ width: '180px' }}
        />
      </FBPageInnerTabsContainer>

      <FBPageTableContainer>
        {selectedTab === 'all' && (
          <ContactsTable
            defaultColumnVisibility={{
              requested_contact__campaign__name: false,
            }}
            tableKey={CONTACTS_TABLE_KEY}
            setTotalRowsCount={setTotalContacts}
            handleContactUpdated={handleContactUpdated}
          />
        )}

        {selectedTab === 'requests' && (
          <ContactRequestsTable tableKey={CONTACT_REQUEST_JOB_TABLE_KEY} />
        )}
      </FBPageTableContainer>
    </FBPage>
  )
}

const Button = styled(FbButton)`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 14px;

  gap: 8px;

  height: 44px;

  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
`
